/**
 * PatientContext - The Global "CurrentPatientContext" Provider
 * 
 * Maintains the "Golden Thread" of patient information throughout the app.
 * When a staff member is within a caseload or admin view, this context
 * automatically tracks the active patient and their complete context.
 */

import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PatientContext = createContext(null);

export function PatientContextProvider({ children }) {
  // Current patient state - the "Golden Thread"
  const [currentPatient, setCurrentPatient] = useState(null);
  const [goldenThread, setGoldenThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // View context - where is the user currently
  const [viewContext, setViewContext] = useState(null); // 'caseload', 'admin_dashboard', 'patient_chart', etc.
  
  // Staff caseload for quick patient selection
  const [caseload, setCaseload] = useState([]);
  
  // Last actions for quick repeat
  const [recentActions, setRecentActions] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("hospice_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  /**
   * Set the active patient and fetch their full context
   */
  const setActivePatient = useCallback(async (patientId, patientBasicInfo = null) => {
    if (!patientId) {
      setCurrentPatient(null);
      setGoldenThread(null);
      return;
    }

    // Set basic info immediately for fast UI response
    if (patientBasicInfo) {
      setCurrentPatient(patientBasicInfo);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/hos/patient-context/full`,
        {
          patient_id: patientId,
          include_intake: true,
          include_admission: true,
          include_diagnoses: true,
          include_staff_assignment: true,
          include_last_contacts: true
        },
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      setCurrentPatient({
        id: patientId,
        ...data.golden_thread?.patient_info
      });
      setGoldenThread(data.golden_thread);
      
      // Save to session storage for persistence across page refreshes
      sessionStorage.setItem("hos_current_patient", JSON.stringify({
        patientId,
        timestamp: new Date().toISOString()
      }));

    } catch (err) {
      console.error("Failed to fetch patient context:", err);
      setError("Failed to load patient context");
      // Keep basic info if available
      if (patientBasicInfo) {
        setCurrentPatient(patientBasicInfo);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  /**
   * Clear the current patient context
   */
  const clearPatientContext = useCallback(() => {
    setCurrentPatient(null);
    setGoldenThread(null);
    sessionStorage.removeItem("hos_current_patient");
  }, []);

  /**
   * Fetch staff caseload for patient selection
   */
  const fetchCaseload = useCallback(async (staffId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/hos/caseload/${staffId}/patients`,
        { headers: getAuthHeaders() }
      );
      setCaseload(response.data.caseload || []);
    } catch (err) {
      console.error("Failed to fetch caseload:", err);
      setCaseload([]);
    }
  }, [getAuthHeaders]);

  /**
   * Execute a command palette action with audit trail
   */
  const executeAction = useCallback(async (actionType, actionData = {}) => {
    if (!currentPatient?.id) {
      return { success: false, error: "No patient selected" };
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/hos/palette-action/execute`,
        {
          action_type: actionType,
          patient_id: currentPatient.id,
          action_data: actionData,
          source: "command_palette"
        },
        { headers: getAuthHeaders() }
      );

      // Track recent action
      setRecentActions(prev => [
        { actionType, patientId: currentPatient.id, timestamp: new Date().toISOString() },
        ...prev.slice(0, 9)
      ]);

      return response.data;
    } catch (err) {
      console.error("Failed to execute action:", err);
      return { success: false, error: err.message };
    }
  }, [currentPatient, getAuthHeaders]);

  /**
   * Get pre-populated form data for documentation
   */
  const getFormPrepopulation = useCallback((formType) => {
    if (!currentPatient || !goldenThread) {
      return null;
    }

    const patientInfo = goldenThread.patient_info || {};
    const diagnoses = goldenThread.diagnoses || {};
    const admission = goldenThread.admission_data || {};
    const lastContacts = goldenThread.last_contacts || [];

    const base = {
      patient_id: currentPatient.id,
      patient_name: patientInfo.name,
      patient_dob: patientInfo.dob,
      patient_mrn: patientInfo.mrn,
      primary_diagnosis: diagnoses.primary?.description,
      primary_icd10: diagnoses.primary?.code,
      level_of_care: admission.level_of_care,
      benefit_period: admission.benefit_period,
      attending_physician: admission.attending_physician,
      created_via: "command_palette"
    };

    // Add form-specific prepopulation
    switch (formType) {
      case "visit_note":
        return {
          ...base,
          patient_header: `${patientInfo.name} | DOB: ${patientInfo.dob} | MRN: ${patientInfo.mrn}`,
          diagnosis_reference: `${diagnoses.primary?.code} - ${diagnoses.primary?.description}`,
          last_contact: lastContacts[0]
        };
      case "phone_log":
        return {
          ...base,
          patient_header: `${patientInfo.name} | DOB: ${patientInfo.dob}`,
          call_purpose_suggestions: ["Check-in", "Symptom update", "Medication question", "Schedule change"],
          last_contact: lastContacts[0]
        };
      case "idg_note":
        return {
          ...base,
          team_members: admission.assigned_team,
          care_plan_summary: goldenThread.care_plan_summary
        };
      default:
        return base;
    }
  }, [currentPatient, goldenThread]);

  /**
   * Bi-directional sync: Refresh patient context when data changes
   * This ensures command palette always has the latest patient info
   */
  const refreshPatientContext = useCallback(async () => {
    if (!currentPatient?.id) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/hos/patient-context/full`,
        {
          patient_id: currentPatient.id,
          include_intake: true,
          include_admission: true,
          include_diagnoses: true,
          include_staff_assignment: true,
          include_last_contacts: true
        },
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      setGoldenThread(data.golden_thread);
    } catch (err) {
      console.error("Failed to refresh patient context:", err);
    }
  }, [currentPatient, getAuthHeaders]);

  /**
   * Subscribe to patient updates (bi-directional sync)
   * Polls every 30 seconds to catch admin dashboard updates
   */
  useEffect(() => {
    if (!currentPatient?.id) return;

    // Initial fetch
    refreshPatientContext();

    // Set up polling interval for bi-directional sync
    const pollInterval = setInterval(() => {
      refreshPatientContext();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [currentPatient?.id, refreshPatientContext]);

  // Restore patient context from session on mount
  useEffect(() => {
    const saved = sessionStorage.getItem("hos_current_patient");
    if (saved) {
      try {
        const { patientId } = JSON.parse(saved);
        if (patientId) {
          setActivePatient(patientId);
        }
      } catch (e) {
        sessionStorage.removeItem("hos_current_patient");
      }
    }
  }, [setActivePatient]);

  const value = {
    // Patient state
    currentPatient,
    goldenThread,
    loading,
    error,
    
    // View context
    viewContext,
    setViewContext,
    
    // Bi-directional sync
    refreshPatientContext,
    
    // Caseload
    caseload,
    fetchCaseload,
    
    // Actions
    setActivePatient,
    clearPatientContext,
    executeAction,
    getFormPrepopulation,
    
    // Recent actions
    recentActions,
    
    // Helper checks
    hasPatientContext: !!currentPatient,
    patientId: currentPatient?.id
  };

  return (
    <PatientContext.Provider value={value}>
      {children}
    </PatientContext.Provider>
  );
}

export function usePatientContext() {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error("usePatientContext must be used within a PatientContextProvider");
  }
  return context;
}

export default PatientContext;
